import React, { lazy, useEffect } from 'react';
import ScopeDefinition from './subcomp/ScopeDefinition';
import { Box, Grid, Card, Tabs, Tab, Stack, Skeleton, Button, Icon } from '@mui/material';
import { IconArrowDown, IconFileTypeXls, IconPlus, IconSearch, IconTrash } from '@tabler/icons-react';
import PageContainer from 'src/components/container/PageContainer';
import ActionIconBtn from 'src/components/shared/form-inputs/ActionIconBtn';
import HideableActionArea from 'src/components/shared/form-inputs/HideableActionArea';
import HoverExpanSearchBtn from 'src/components/shared/form-inputs/HoverExpanSearchBtn';
import { AppState, dispatch, useSelector } from 'src/store/Store';
import TargetsTable from './subcomp/TargetsTable';
import { setActionButtonStates, setBreadcrumb, setPersist, setShowScanDomainDialog, setToastMessage } from 'src/store/my_pages/app';
import { setTargetsSearchForm, targetsClearForm } from 'src/store/my_pages/targets';
import NoDomainAddedInfoCard from 'src/components/shared/NoDomainAddedInfoCard';
import { fetchSingleApi } from 'src/App';
import { exportToExcel } from 'src/utils/utils';
import { a11yProps, CustomTabPanel } from 'src/components/shared/CustomTabs';
import { width } from '@mui/system';
import { apiConfig, getApiUrl } from 'src/config/apiConfig';
import axiosIn from 'src/middlewares/axiosInterceptor';

const TargetsPage = () => {
  const tar_Rx = useSelector((state: AppState) => state.targetsReducer);
  const { persist, isLoadings, is_initial_fetches_loading } = useSelector((state: AppState) => state.appGeneralReducer);
  const authReducer = useSelector((state: AppState) => state.authReducer);

  useEffect(() => {
    const BCrumb = [
      {
        to: '/',
        title: 'Home',
      },
      {
        title: 'Targets',
      },
    ];
    dispatch(setBreadcrumb(BCrumb));
  }, [dispatch]);

  const [tabId, setTabId] = React.useState(0);
  const handleTabChange = (event: React.SyntheticEvent, newValue: number) => {
    setTabId(newValue);
  };

  const searchFormContent = [
    // Not used keys = id, name, creationName userDomainId scanFrequency isPaused manualStartTime
    { name: 'Target', type: 'text', reskey: 'url' },
    { name: 'HTTP Headers', type: 'boolean', reskey: 'scanHeader' },
    { name: 'Ports', type: 'boolean', reskey: 'scanPort' },
    { name: 'Vulnerabilities', type: 'boolean', reskey: 'scanVulnerabilities' },
    { name: 'Subdomain', type: 'boolean', reskey: 'scanSubdomain' },
    { name: 'Subdomain Http Header', type: 'boolean', reskey: 'scanSubdomainHeader' },
    { name: 'Subdomain Port', type: 'boolean', reskey: 'scanSubdomainPort' },
    { name: 'Last Execution Time', type: 'datetime', reskey: 'lastExecutionTime' },
    { name: 'Next Scan', type: 'datetime', reskey: 'nextExecutionTime' },
    { name: 'Manual Scan', type: 'text', reskey: 'isManualStartAvailable' },
    {
      name: '',
      title: 'Search',
      type: 'action-button',
      icon: IconSearch,
      colorj: 'info',
      variant: 'outlined',
      onClick: () => dispatch(setToastMessage('SEARCH REQUEST')),
      sx: { paddingX: 1, paddingY: '11px !important', top: '3px', marginRight: '5px', minWidth: 'min-content' },
    },
    {
      name: '',
      title: 'Clear',
      type: 'action-button',
      icon: IconTrash,
      colorj: 'error',
      variant: 'outlined',
      onClick: () => dispatch(targetsClearForm()),
      sx: { paddingX: 1, paddingY: '11px !important', top: '3px', marginRight: '5px', minWidth: 'min-content' },
    },
  ];

  async function handleDownload_xlsx() {
    const alldata_without_pagination = persist?.schedule_scan;
    if (!alldata_without_pagination?.length) {
      console.warn('No data available to export');
      return;
    }

    // Define all possible scan types in one place
    const SCAN_NAMES = [
      'DNS Integrity Check',
      'Vulnerability Detection',
      'Security Headers Validator',
      'Security Subdomain Headers Validator',
      'Port Scanner',
      'Subdomain Port Scanner',
      'SSL/TLS Configuration Audit',
      'Subdomain SSL/TLS Configuration Audit',
      'Malicious Content Detection',
      'Subdomain Malicious Content Detection',
      'Hidden Paths & File Detection',
      'Subdomain Hidden Paths & File Detection',
      'Secrets & Sensitive Data Finder',
      'Subdomain Secrets & Sensitive Data Finder',
      'Resource Takeover Defense',
    ];

    // Create a mapping function for scan checks
    const createScanCheck = (scanType: string) => (item: any) => item.scanPackageDetail?.includedScans?.some((i: any) => i.label === scanType);

    // Prepare worksheet data with optimized mapping
    const worksheetData = alldata_without_pagination.map((item) => {
      const baseData = {
        URL: item.url,
        'Last Execution Time': item.lastExecutionTime,
        'Next Execution Time': item.nextExecutionTime,
      };

      // Dynamically add scan type checks
      const scanData = SCAN_NAMES.reduce((acc, scanType) => {
        acc[scanType] = createScanCheck(scanType)(item);
        return acc;
      }, {} as Record<string, boolean>);

      return { ...baseData, ...scanData };
    });

    exportToExcel({
      data: worksheetData,
      fileName: 'SecuSeek-Targets',
      sheetName: 'Scan Results',
    });
  }

  // Fetch scan list (avoid duplicate request if data already present)
  useEffect(() => {
    const scanlistfetch = async () => {
      await fetchSingleApi('schedule_scan');
    };
    // Only fetch if we have auth token and schedule_scan data is undefined (not yet fetched)
    // Don't refetch if it's an empty array (no targets) as that's a valid state
    if (authReducer?.authToken && persist?.schedule_scan === undefined) {
      scanlistfetch();
    }
  }, [authReducer?.authToken, persist?.schedule_scan]);

  // Polling for incomplete scans when realtime is not connected
  React.useEffect(() => {
    let pollInterval: NodeJS.Timeout | null = null;

    const incompletedItems = Array.isArray(persist?.schedule_scan) && persist.schedule_scan.filter((item: any) => item?.status?.isCompleted !== true);

    // Skip polling if realtime is connected
    if (persist?.realtime_connected === true) {
      return () => {
        if (pollInterval) clearTimeout(pollInterval);
      };
    }

    // Start polling for incomplete items when realtime is not connected
    if (incompletedItems?.length) {
      pollInterval = setInterval(async () => {
        try {
          const updatedItems = await Promise.all(
            incompletedItems.map(async (j: any) => {
              const curl = getApiUrl(apiConfig.scheduled_scan, { id: j.id });
              const response = await axiosIn.get(curl);
              return response.data;
            }),
          );
          const newScheduledScanList = (persist?.schedule_scan || []).map((item: any) => {
            const updated = updatedItems.find((u) => u.id === item.id);
            return updated ? updated : item;
          });
          dispatch(setPersist({ key: 'schedule_scan', value: newScheduledScanList }));
        } catch (error) {
          console.error('TargetsPage: Polling error:', error);
        }
      }, 7000);
    }

    return () => {
      if (pollInterval) {
        clearTimeout(pollInterval);
      }
    };
  }, [persist?.schedule_scan, persist?.realtime_connected]);

  const isLoading = isLoadings?.schedule_scan === true || is_initial_fetches_loading === true;
  const hasData = Array.isArray(persist?.schedule_scan) && persist?.schedule_scan.length > 0;

  if (isLoading) {
    return (
      <Box width={'100%'} mt={4}>
        <Skeleton
          sx={{
            width: '100%',
            minWidth: '50vw',
            height: 400,
            maxHeight: '50vh',
            transform: 'none',
            mt: `4rem`,
          }}
        />
      </Box>
    );
  }

  if (!isLoading && !hasData) {
    return <NoDomainAddedInfoCard />;
  }

  if (!isLoading && hasData) {
    return (
      <PageContainer title="Targets" description="This is Targets page">
        <Box>
          {/* <Grid container spacing={3} justifyContent={'start'}>
            <Grid item sx={{ display: 'flex', alignItems: 'stretch' }}>
              {!persist?.is_initial_fetches_loading ? (
                <ScopeDefinition />
              ) : (
                <Box width={'100%'}>
                  <Skeleton
                    sx={{
                      width: '100%',
                      minWidth: '50vw',
                      height: 100,
                      maxHeight: '50vh',
                      transform: 'none',
                    }}
                  />
                </Box>
              )}
            </Grid>
          </Grid> */}
          {/* ---- */}
          <Grid sx={{ display: 'flex', alignItems: 'stretch', gap: 2, width: '100%' }} size={12}>
            {!persist?.is_initial_fetches_loading ? (
              <Card sx={{ display: 'flex', flexDirection: 'column', gap: 2, padding: 1 }}>
                <Box sx={{ width: '100%' }}>
                  <Box
                    sx={{
                      borderBottom: 1,
                      borderColor: 'divider',
                      width: '100%',
                      display: 'flex',
                      alignItems: { xs: 'flex-start', md: 'space-between' },
                      justifyContent: { xs: 'flex-start', md: 'space-between' },
                      flexDirection: { xs: 'column-reverse', md: 'row' },
                    }}
                  >
                    <Tabs value={tabId} onChange={handleTabChange} aria-label="basic tabs example" sx={{ padding: 1 }}>
                      <Tab label="Your Scopes" {...a11yProps(0)} />
                    </Tabs>
                    <Box
                      sx={{
                        marginRight: { xs: 0, md: 0 },
                        display: 'flex',
                        alignItems: 'center',
                        marginBottom: '10px',
                        alignSelf: { xs: 'end', md: 'start' },
                      }}
                    >
                      <Box px={0} py={1} sx={{ display: 'flex', gap: { xs: 1, md: 2 }, justifyContent: 'center', alignItems: 'center', padding: 0, marginRight: { xs: 0, md: 2 } }}>
                        <HoverExpanSearchBtn searchKey="targets" placeholder={'Search domain'} />
                        <Stack direction="row" alignItems={'center'} sx={{ gap: { xs: 1, md: 2 } }}>
                          {/* <ActionIconBtn icon={IconFilter} tooltipTitle={'Filter'} onClick={() => dispatch(setActionButtonStates('filter'))} /> */}
                          {/* <ActionIconBtn icon={IconArrowDown} tooltipTitle={'Order'} onClick={() => dispatch(setActionButtonStates('order'))} /> */}
                          <ActionIconBtn icon={IconFileTypeXls} tooltipTitle={'Download XLS'} onClick={() => handleDownload_xlsx()} sx={{}} />
                          <Button
                            variant="contained"
                            onClick={() => dispatch(setShowScanDomainDialog(true))}
                            sx={{
                              display: { xs: 'none', md: 'flex' },
                              px: 2,
                              py: 0.5,
                              minWidth: 120,
                              whiteSpace: 'nowrap',
                              flexShrink: 0,
                              borderRadius: '8px',
                              backgroundColor: 'primary.main', // theme.palette.purple.A400
                              color: 'primary.contrastText',
                              '&:hover': {
                                backgroundColor: 'primary.dark', // theme.palette.purple.A75
                                color: 'primary.contrastText',
                              },
                            }}
                          >
                            <IconPlus stroke={1.3} size="18" />
                            Add New
                          </Button>
                          <Box
                            onClick={() => dispatch(setShowScanDomainDialog(true))}
                            sx={{
                              display: { xs: 'flex', md: 'none' },
                              // ml: 2,
                              // px: 2,
                              // py: 1,
                              // minWidth: 'min-content',
                              // borderRadius: '8px',
                              // backgroundColor: 'primary.main', // theme.palette.purple.A400
                              // color: 'primary.contrastText',
                              // '&:hover': {
                              //   backgroundColor: 'primary.dark', // theme.palette.purple.A75
                              //   color: 'primary.contrastText',
                              // },
                            }}
                          >
                            <ActionIconBtn
                              icon={IconPlus}
                              tooltipTitle={'Add new target'}
                              onClick={() => dispatch(setShowScanDomainDialog(true))}
                              sx={{ backgroundColor: 'primary.main', color: 'primary.contrastText' }}
                            />
                          </Box>
                        </Stack>
                      </Box>
                      {/* <>
                      <UploadFileOutlined /> {row.file ? t('Change file') : t('Attach file')}
                      <input hidden accept="image/*,.xlsx,.xls,.doc,.docx,.pdf" type="file" onChange={(e: any) => handleFileChange(e, row.id)} />
                    </> */}
                    </Box>
                  </Box>
                  <Box>
                    {/* sx={{ minHeight: '400px' }} */}
                    <CustomTabPanel value={tabId} index={0}>
                      <Box>
                        <HideableActionArea
                          isVisible={tar_Rx?.actionButtonStates.filter.show}
                          height={'100px'}
                          inputAr={searchFormContent}
                          rxVal={tar_Rx}
                          rxSetVal={(key: string, value: any) => dispatch(setTargetsSearchForm({ key, value }))}
                        />
                        <TargetsTable tabId={'tabId_main'} />
                      </Box>
                    </CustomTabPanel>
                  </Box>
                </Box>
              </Card>
            ) : (
              <Skeleton
                sx={{
                  width: '100%',
                  height: 400,
                  maxHeight: '50vh',
                  transform: 'none',
                }}
              />
            )}
          </Grid>
          {/* </Grid> */}
        </Box>
      </PageContainer>
    );
  }

  return <NoDomainAddedInfoCard />;
};

export default TargetsPage;
